import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import debounce from 'lodash/debounce';

import { Restaurant, RestaurantAddress, CustomerDeliveryAddress, FacModalContent } from '@/models';
import { FACOptions, ui as UiConstants, countries } from '@/constants';
import { Modals } from '@/store/ui';
import { AnalyticsEventCategory, AnalyticsEventAction } from '@/services/google/google-analytics.services';
import { Routes } from '@/router';

import FindAChipotleSearch from './CmgFindAChipotleSearch/cmg-find-a-chipotle-search';
import trim from 'lodash/trim';
import defer from 'lodash/defer';

const PIN_TYPE: { [key: string]: string; } = {
  CLOSED: 'closed',
  FAVORITE: 'favorite',
  DEFAULT: 'default',
  CHIPOTLANE: 'chipotlane',
  CHIPOTLANE_FAV: 'chipotlaneFav',
  CHIPOTLANE_CLOSED: 'chipotlaneClosed',
  DIGITAL_KITCHEN: 'digitalKitchen',
  DIGITAL_KITCHEN_FAV: 'digitalKitchenFav',
  DIGITAL_KITCHEN_CLOSED: 'digitalKitchenClosed',
  WALKUP_WINDOW: 'walkupWindow',
  WALKUP_WINDOW_FAV: 'walkupWindowFav',
  WALKUP_WINDOW_CLOSED: 'walkupWindowClosed'
};
const AUTOCOMPLETE_PLACE_TYPE_PICKUP = '(regions)';
const AUTOCOMPLETE_PLACE_TYPE_DELIVERY = 'address';
const MAX_SEARCH_THIS_AREA_ZOOM = 9;
const MOBILE_MAP_HEIGHT_DEFAULT = 320;
const MOBILE_MAP_HEIGHT_DEFAULT_SMALL = 220;

@Component({
  name: 'CmgFindAChipotleModal',
})
export default class CmgFindAChipotleModal extends Vue {
  showPickup = true;
  placeType = AUTOCOMPLETE_PLACE_TYPE_PICKUP;
  searchThisAreaEnabled = false;
  showNoSuggestions = false;
  isValidDeliveryAddress = false;
  pickupSearchInput = '';
  lastPickupSearchGeo: google.maps.LatLng | null | undefined = null;
  deliverySearchInput = '';
  currentDeliveryAddress: CustomerDeliveryAddress | null = null;
  showDeliveryAddressValidationResult = false;
  mapHeightOffset = false;
  mapStyles: { height?: string; flex?: string; } = { height: '100% !important' };
  hasEmptySearchResults = false;
  hideLocationBanner = true;
  locationBannerTimeout: number | undefined = undefined;
  map: google.maps.Map = null as any;
  facModalContent: FacModalContent | null = null;

  private markerIcons!: { [key: string]: google.maps.Icon; };

  private mapSelectedRestaurant: Restaurant | null = null;
  private markers: {
    [key: number]: {
      markerObj: google.maps.Marker;
      markerType: string;
      markerRestaurant: Restaurant;
    };
  } = {};
  private deliveryMarker: google.maps.Marker | null = null;

  private debouncedGooglePlacesSearch = debounce(this.searchGooglePlaces, 200);
  private autoCompleteSearchResults?: QueryAutocompletePrediction[] = [];

  private mobileMapHeight = MOBILE_MAP_HEIGHT_DEFAULT;
  private mobileMapOffset = 0;
  private dotsAreDragging = false;

  private debouncedBannerClose = debounce(() => {
    this.onCloseBanner();
  }, 2600);

  get visible(): boolean {
    //@TODO: move this to its own function?
    if(this.$appStore.state.ui.modals.findAChipotle.visible){
      if(!this.$services.config.getPickupOptions() || this.$services.config.getPickupOptions().length === 0){
        this.$services.config.setPickupOptions()
      }
    }

    return this.$appStore.state.ui.modals.findAChipotle.visible;
  }

  get restaurantLocations(): Restaurant[] {
    return this.$appStore.state.restaurant.restaurantSearchResults;
  }

  get favoriteRestaurantIds(): { [key: number]: string; } {
    return this.$appStore.state.customer.favoriteRestaurants;
  }

  get favoriteRestaurants(): Restaurant[] {
    return this.$appStore.state.restaurant.restaurantFavorites;
  }

  get recentRestaurant(): Restaurant | null {
    return this.$appStore.state.restaurant.mostRecentRestaurant;
  }

  get selectedPickupRestaurant() {
    return this.$appStore.state.restaurant.selectedRestaurant;
  }

  get selectedDeliveryAddress() {
    return this.$appStore.state.order.selectedDeliveryAddress;
  }

  get searchThisAreaActive() {
    return this.searchThisAreaEnabled && this.showPickup && this.map && this.map.getZoom() > MAX_SEARCH_THIS_AREA_ZOOM;
  }

  get args() {
    return this.$appStore.state.ui.modals.findAChipotle.args;
  }

  get facOptions() {
    return countries[this.$appStore.state.ui.countryProfile.countryCode.toLocaleLowerCase()].facOptions;
  }

  @Watch('args', { immediate: true })
  onArgsChanged(args: Modals['findAChipotle']['args']) {
    if (!args) {
      return;
    }

    // This check is to appease typescript, can't use includes or type definition of specific values
    if (args.mode == 'pickup' || args.mode == 'delivery') {
      // Deferring to let the stack on the event loop clear so the ui toggle is loaded
      defer(mode => this.onTogglePickupDelivery(mode), args.mode);
      if (args.deliverySearchInput) {
        defer(deliverySearchInput => {
          this.onSearchInput(deliverySearchInput);
          (this.$refs.findAChipotleSearch as FindAChipotleSearch).onSearchSubmit();
          (this.$refs.findAChipotleSearch as FindAChipotleSearch).setShowAutocomplete(false);
        }, args.deliverySearchInput);
      }
    }

    if (args.pickupSearchInput) {
      this.pickupSearchInput = args.pickupSearchInput;
    } else if (args.deliverySearchInput) {
      this.deliverySearchInput = args.deliverySearchInput;
    }
  }

  @Watch('restaurantLocations', { immediate: false, deep: true })
  onRestaurantLocationChanged(newRestaurants: Restaurant[]) {
    if (this.map) {
      this.hasEmptySearchResults = newRestaurants.length === 0 && this.pickupSearchInput !== '';
      this.flushAndUpdateMarkers(newRestaurants);
    }
  }

  @Watch('favoriteRestaurantIds', { immediate: false, deep: true })
  onFavoritesChanged(newFavorites: { [key: number]: string; }) {
    // handle the inherent non-reactiveness of google maps pins
    for (const markerIdx of (Object.keys(this.markers) as any) as number[]) {
      if (newFavorites[markerIdx] != null) {
        // recreate as favorite
        this.markers[markerIdx].markerObj.setMap(null);
        this.addPin(this.markers[markerIdx].markerRestaurant, this.getFavPinType(this.markers[markerIdx].markerRestaurant));
      } else if (newFavorites[markerIdx] == null) {
        // recreate as non-favorite
        this.markers[markerIdx].markerObj.setMap(null);
        this.addPin(this.markers[markerIdx].markerRestaurant, this.getDefaultPinType(this.markers[markerIdx].markerRestaurant));
      }
    }
  }

  @Watch('visible', { immediate: false })
  async onVisible(newVisible: boolean) {
    if (newVisible === false) {
      this.$services.restaurant.clearSearchResults();
      this.mapSelectedRestaurant = null;
      this.pickupSearchInput = '';
      this.lastPickupSearchGeo = null;
      this.deliverySearchInput = '';
      this.currentDeliveryAddress = null;
      this.autoCompleteSearchResults = [];
      this.dotsAreDragging = false;
      this.mobileMapHeight = MOBILE_MAP_HEIGHT_DEFAULT;
      this.showDeliveryAddressValidationResult = false;
      this.hasEmptySearchResults = false;
      return;
    } else {
      let facModalContent = this.$services.config.getFacModalContent();
      if (!facModalContent) {
        await this.$services.config.setFacModalContent();
        facModalContent = this.$services.config.getFacModalContent();
      }
      this.facModalContent = facModalContent || null;
    }

    this.forceMapSizeRecalibration();

    //If modal is visible and selected delivery address, initiate find a chipotle delivery flow
    if (this.map && this.selectedDeliveryAddress && !this.args){
      defer(() => this.onFindAChipotleButton());
    }
  }

  @Watch('visible', { immediate: true, deep: true })
  async visibleChanged(newVisible: boolean) {
    if (!this.$appStore.state.ui.modals.toast.visible) {
      if (newVisible) {
        this.$services.ui.nextFocus('chipotle-search');
      }
    } else {
      Vue.nextTick(() => {
        setTimeout(() => {
          const counter = document.querySelectorAll('.find-a-chipotle-search').length;
          if (counter <= 1) {
            const cfocus = document.querySelectorAll('.find-a-chipotle-search')[0];
            const selection = document.querySelector('[tabindex = "0"]') !== null;
            if (selection && cfocus) {
              const focusTarget = cfocus.querySelector('[tabindex = "0"]') as HTMLElement;
              focusTarget.focus();
            }
          }
        }, 7000);
      });
    }
  }

  forceMapSizeRecalibration() {
    this.toggleMapRefreshPixel(333);
    this.toggleMapRefreshPixel(1200);
    this.toggleMapRefreshPixel(5000);
  }

  toggleMapRefreshPixel(timeout: number) {
    // HACK: wiggle the div height to force the google maps refresh.
    // This is to address a bug where maps initializes while the modal is hidden,
    // locks onto the wrong size and loads only map tiles for the smaller size.
    setTimeout(() => {
      this.mapHeightOffset = true;
      this.mobileMapOffset = 1;
      this.refreshMapHeightStyles();
    }, timeout);

    setTimeout(() => {
      this.mapHeightOffset = false;
      this.mobileMapOffset = 0;
      this.refreshMapHeightStyles();
    }, timeout + 25);
  }

  mounted() {
    if (window.innerWidth < 450) {
      this.mobileMapHeight = MOBILE_MAP_HEIGHT_DEFAULT_SMALL;
    }

    this.refreshMapHeightStyles();
    window.addEventListener(
      'resize',
      debounce(() => {
        this.refreshMapHeightStyles();
      }, 500),
    );
  }

  async onCloseModal() {
    const args = this.args;
    const path = this.$route.fullPath;
    //don't use args.onClose() in order pages, breaks or returns to previous page in browser when it shouldn't
    if (args && args.onClose && !path.includes('order')) {
      args.onClose();
    }
    this.$services.ui.setModalVisibility('findAChipotle', false);
    this.$services.ui.focusBack();

    if (this.selectedPickupRestaurant) {
      const epops = await this.$services.epop.getEpopsForRestaurant(this.selectedPickupRestaurant.restaurantNumber);
      if (epops && epops.length > 0) {
        this.$services.ui.setModalVisibility('epop', true, {
          epops,
        });
      }
    }
    if(path.includes('/order/build/family-meals') || path.includes('/order/build/high-protein-menu')){
      this.$router.push({ name: Routes.Home });
    }
    
    if(location.pathname === '/find-a-chipotle'){
      this.$router.replace({ name: Routes.Home, hash: '#menu' });
    }
  }

  async onTogglePickupDelivery(searchState: 'pickup' | 'delivery') {
    if (searchState === 'pickup') {
      this.placeType = AUTOCOMPLETE_PLACE_TYPE_PICKUP;
      this.showPickup = true;
      if (this.lastPickupSearchGeo) {
        this.setMapCenter(this.lastPickupSearchGeo);
        this.map.setZoom(12);
      }
      this.flushAndUpdateMarkers(this.restaurantLocations);
      this.removeDeliveryPin();
    } else {
      this.placeType = AUTOCOMPLETE_PLACE_TYPE_DELIVERY;
      this.showPickup = false;
      this.deleteMarkers();
      this.addDeliveryPin(this.currentDeliveryAddress);
    }
    this.autoCompleteSearchResults = [];
    this.$services.featureFlags.refreshApimFeatureFlags();
  }

  onShowDeliveryAddressValidationResult(state: boolean) {
    this.showDeliveryAddressValidationResult = state;
  }

  onSelectDeliveryAddress(deliveryAddress: CustomerDeliveryAddress) {
    this.deliverySearchInput = `${trim(deliveryAddress.streetAddress1)}, ${trim(deliveryAddress.city)}, ${trim(deliveryAddress.state)}`;
    this.currentDeliveryAddress = deliveryAddress;
    this.addDeliveryPin(deliveryAddress);
  }

  onMouseoverRestaurant(restaurant: Restaurant) {
    const marker = this.markers[restaurant.restaurantNumber];
    if (marker) {
      // marker will only exist if it's in current map view
      marker.markerObj.setIcon(this.markerIcons[marker.markerType + '2x']);
    }
  }

  onMouseleaveRestaurant(restaurant: Restaurant) {
    // make the currently selected pin small again
    if (restaurant && this.markers[restaurant.restaurantNumber] != null) {
      this.markers[restaurant.restaurantNumber].markerObj.setIcon(this.markerIcons[this.markers[restaurant.restaurantNumber].markerType]);
    }
  }

  async onSelectRestaurant(selectedRestaurant: Restaurant) {
    if (this.mapSelectedRestaurant != null && this.markers[this.mapSelectedRestaurant.restaurantNumber] != null) {
      // make the currently selected pin small again
      this.markers[this.mapSelectedRestaurant.restaurantNumber].markerObj.setIcon(this.markerIcons[this.markers[this.mapSelectedRestaurant.restaurantNumber].markerType]);
    }

    if (selectedRestaurant != null && !!selectedRestaurant.address) {
      const address = selectedRestaurant.address;
      this.$services.order.setCachedRestaurant(selectedRestaurant);
      this.pickupSearchInput = `${trim(address.addressLine1)}, ${trim(address.locality)}, ${trim(address.administrativeArea)}`;
      this.autoCompleteSearchResults = [];
    }

    if (selectedRestaurant == null) {
      // we're deselecting a restaurant
      this.mapSelectedRestaurant = null;
      this.$services.order.setCachedRestaurant(null);
      return;
    }

    // if the marker doesnt exist or if it isnt in the view or if the map is just zoomed out too far
    const isSelectedRestaurantAddressDefined = selectedRestaurant.address && selectedRestaurant.address.latitude && selectedRestaurant.address.longitude;
    const isSelectedMarkerInMap = this.markers[selectedRestaurant.restaurantNumber]
      && this.map.getBounds()!.contains(this.markers[selectedRestaurant.restaurantNumber].markerObj.getPosition() as google.maps.LatLng)
      && this.map.getZoom() >= 10;
    if (isSelectedRestaurantAddressDefined && !isSelectedMarkerInMap) {
      // zoom to selected pickup location
      const restaurantLocation = new google.maps.LatLng(selectedRestaurant.address.latitude!, selectedRestaurant.address.longitude!);
      this.setMapCenter(restaurantLocation);
      this.searchThisAreaEnabled = false;

      // search location
      await this.$services.restaurant.restaurantSearch(restaurantLocation);
    }

    const marker = this.markers[selectedRestaurant.restaurantNumber];
    if( marker) {
      marker.markerObj.setIcon(this.markerIcons[marker.markerType + '2x']);
      this.mapSelectedRestaurant = marker.markerRestaurant;
    }
  }

  async onFindAChipotleButton() {
    this.searchThisAreaEnabled = false;
    if (this.selectedDeliveryAddress != null) {
      // clear pickup pins and data, select delivery tab
      this.$services.restaurant.clearSearchResults();
      this.onTogglePickupDelivery('delivery');
      const addr = this.selectedDeliveryAddress;
      this.deliverySearchInput = `${addr.streetAddress1}, ${addr.city}, ${addr.state}, ${addr.postalCode}`;
      await this.searchGooglePlaces(this.deliverySearchInput, this.placeType);
      // HACK: calling CmgFindAChipotleSearch.onSearchSubmit through the ref is a bit of a hack but results in much less code
      (this.$refs.findAChipotleSearch as FindAChipotleSearch).onSearchSubmit();
    } else if (this.selectedPickupRestaurant != null) {
      // select pickup tab and clear deliver pin
      this.onTogglePickupDelivery('pickup');
      this.showDeliveryAddressValidationResult = false;

      // select restaurant
      this.onSelectRestaurant(this.selectedPickupRestaurant);
    }
  }

  onSearchInput(searchString: string) {
    if (this.showPickup) {
      this.pickupSearchInput = searchString;
    } else {
      this.deliverySearchInput = searchString;
    }

    if (!searchString || searchString.length < 3) {
      this.autoCompleteSearchResults = [];
      this.showNoSuggestions = false;
      return;
    }

    this.debouncedGooglePlacesSearch(searchString, this.placeType);
  }

  async searchGooglePlaces(searchString: string, placeType: string) {
    try {
      this.autoCompleteSearchResults = await this.$services.googleMaps.googlePlacesAutoComplete(searchString, placeType);
    } catch (e: any) {
      if (e !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        this.$services.logger.error('Google AutoComplete failed');
      }
      // TODO-8bt25m: address this show stopper
    }
    this.showNoSuggestions = searchString.length > 2 && (!this.autoCompleteSearchResults || !this.autoCompleteSearchResults.length);
  }

  async onSearchSubmit(searchValue: string) {
    this.lastPickupSearchGeo = await this.$services.googleMaps.googleGeocodeSearchString(searchValue);
    if (this.lastPickupSearchGeo) {
      this.setMapCenter(this.lastPickupSearchGeo);
      if (this.placeType === AUTOCOMPLETE_PLACE_TYPE_PICKUP) {
        this.searchThisArea();
      }
    }
  }

  onClearSearch() {
    if (this.showPickup) {
      this.pickupSearchInput = '';
      this.$services.restaurant.clearSearchResults();
      this.hasEmptySearchResults = false;
      return;
    }
    this.deliverySearchInput = '';
    this.removeDeliveryPin();
    this.showDeliveryAddressValidationResult = false;
  }

  async onMapElementMounted(element: any) {
    try {
      // TODO-rzqpbh: if this import fails we need to halt the page and display an error.
      await this.$services.googleMaps.getGoogleMaps();
      // Wait for google maps to resolve before doin' the prepopulated search
      if (this.pickupSearchInput) {
        this.onSearchSubmit(this.pickupSearchInput);
      }

      this.map = new google.maps.Map(element, {
        center: { lat: this.$appStore.state.ui.countryProfile.lat, lng: this.$appStore.state.ui.countryProfile.lng },
        zoom: this.facOptions.googleMapsZoomLevel,
        minZoom: 4,
        disableDefaultUI: true,
        noClear: true,
      });

      this.markerIcons = {
        default: {
          url: this.$appStore.state.cms.images.icons.pepperMarker.default,
          scaledSize: new google.maps.Size(60, 60),
        },
        default2x: {
          url: this.$appStore.state.cms.images.icons.pepperMarker.default,
          scaledSize: new google.maps.Size(90, 90),
        },
        chipotlane: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerChipotlane.default,
          scaledSize: new google.maps.Size(84, 76),
        },
        chipotlane2x: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerChipotlane.default,
          scaledSize: new google.maps.Size(126, 116),
        },
        chipotlaneFav: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerChipotlaneFav.default,
          scaledSize: new google.maps.Size(84, 76),
        },
        chipotlaneFav2x: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerChipotlaneFav.default,
          scaledSize: new google.maps.Size(126, 116),
        },
        chipotlaneClosed: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerChipotlaneClosed.default,
          scaledSize: new google.maps.Size(84, 76),
        },
        chipotlaneClosed2x: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerChipotlaneClosed.default,
          scaledSize: new google.maps.Size(126, 116),
        },
        closed: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerClosed.default,
          scaledSize: new google.maps.Size(69, 64),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(43, 64),
        },
        closed2x: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerClosed.default,
          scaledSize: new google.maps.Size(103, 96),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(67, 96),
        },
        favorite: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerFave.default,
          scaledSize: new google.maps.Size(60, 60),
        },
        favorite2x: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerFave.default,
          scaledSize: new google.maps.Size(90, 90),
        },
        digitalKitchen: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerDK.default,
          scaledSize: new google.maps.Size(110, 77),
        },
        digitalKitchen2x: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerDK.default,
          scaledSize: new google.maps.Size(149, 104),
        },
        digitalKitchenFav: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerDKFavorite.default,
          scaledSize: new google.maps.Size(110, 77),
        },
        digitalKitchenFav2x: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerDKFavorite.default,
          scaledSize: new google.maps.Size(149, 104),
        },
        digitalKitchenClosed: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerDKClosed.default,
          scaledSize: new google.maps.Size(110, 77),
        },
        digitalKitchenClosed2x: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerDKClosed.default,
          scaledSize: new google.maps.Size(149, 104),
        },
        walkupWindow: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerWalkup.default,
          scaledSize: new google.maps.Size(70, 81),
        },
        walkupWindow2x: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerWalkup.default,
          scaledSize: new google.maps.Size(103, 120),
        },
        walkupWindowFav: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerWalkupFavorite.default,
          scaledSize: new google.maps.Size(70, 81),
        },
        walkupWindowFav2x: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerWalkupFavorite.default,
          scaledSize: new google.maps.Size(103, 120),
        },
        walkupWindowClosed: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerWalkupClosed.default,
          scaledSize: new google.maps.Size(70, 81),
        },
        walkupWindowClosed2x: {
          url: this.$appStore.state.cms.images.icons.pepperMarkerWalkupClosed.default,
          scaledSize: new google.maps.Size(70, 81),
        },
        deliveryMarker: {
          url: this.$appStore.state.cms.images.icons.deliveryMarker.default,
          scaledSize: new google.maps.Size(90, 90),
        },
      };

      this.map.addListener('center_changed', () => {
        if (this.map.getZoom() > MAX_SEARCH_THIS_AREA_ZOOM) {
          this.searchThisAreaEnabled = true;
        }
      });

      this.map.addListener('zoom_changed', () => {
        this.searchThisAreaEnabled = this.map.getZoom() > MAX_SEARCH_THIS_AREA_ZOOM;
      });

      this.onVisible(true);
    } catch (error: any) {
      this.$services.logger.error('Something went wrong trying to render google maps', error);
    }
  }

  zoomIn() {
    this.map.setZoom(this.map.getZoom() + 1);
  }

  zoomOut() {
    this.map.setZoom(this.map.getZoom() - 1);
  }

  setMapCenter(location: google.maps.LatLng) {
    this.map.panTo(location);
    this.map.setZoom(12);
  }

  onFindNearby() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          this.lastPickupSearchGeo = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
          this.setMapCenter(this.lastPickupSearchGeo);
          this.searchThisArea();
        },
        err => {
          this.requestLocationMessage();
        },
      );
    } else {
      this.requestLocationMessage();
    }
  }

  requestLocationMessage() {
    this.hideLocationBanner = false;

    this.debouncedBannerClose();
  }

  onCloseBanner() {
    this.hideLocationBanner = true;
  }

  addDeliveryPin(deliveryAddress: CustomerDeliveryAddress | null) {
    this.deleteMarkers();
    this.removeDeliveryPin();

    if (!deliveryAddress || !deliveryAddress.latitude || !deliveryAddress.longitude) {
      return;
    }

    this.deliveryMarker = new google.maps.Marker({
      position: { lat: deliveryAddress.latitude, lng: deliveryAddress.longitude },
      map: this.map,
      icon: this.markerIcons['deliveryMarker'],
    });

    const center = new google.maps.LatLng(deliveryAddress.latitude, deliveryAddress.longitude);
    this.setMapCenter(center);
  }

  removeDeliveryPin() {
    if (this.deliveryMarker != null) {
      this.deliveryMarker.setMap(null);
      this.deliveryMarker = null;
    }
  }

  addPin(location: Restaurant, type: string) {
    if( !(location.address && location.address.longitude && location.address.latitude)) {
      console.warn('address missing for restaurant', location.restaurantName, "#", location.restaurantNumber);
      return;
    }
    const address = location.address as RestaurantAddress;
    const newMarker = new google.maps.Marker({
      position: { lat: address.latitude!, lng: address.longitude! },
      map: this.map,
      icon: this.markerIcons[type],
    }) as { restaurantNumber: number; } & google.maps.Marker;

    newMarker.restaurantNumber = location.restaurantNumber;
    this.markers[location.restaurantNumber] = {
      markerObj: newMarker,
      markerType: type,
      markerRestaurant: location,
    };
    newMarker.addListener('click', () => {
      this.onSelectRestaurant(this.markers[newMarker.restaurantNumber].markerRestaurant);
    });
  }

  // Deletes all markers in the array by removing references to them.
  deleteMarkers() {
    for (const markerIdx of (Object.keys(this.markers) as any) as number[]) {
      if( this.markers[markerIdx] ) {
        this.markers[markerIdx].markerObj.setMap(null);
      }
    }
    this.markers = {};
  }

  async searchThisArea() {
    const mapCenter = this.map.getCenter() || new google.maps.LatLng(this.$appStore.state.ui.countryProfile.lat, this.$appStore.state.ui.countryProfile.lng);

    await this.$services.restaurant.restaurantSearch(mapCenter);

    this.map.setZoom(12);
    this.searchThisAreaEnabled = false;

    this.$services.googleAnalytics.sendCustomEvent({ category: AnalyticsEventCategory.Restaurant, action: AnalyticsEventAction.SearchSubmit });
    // TODO-gqnd44: it would look really slick to move to a zoom and center based on the found restaurants
  }

  flushAndUpdateMarkers(newRestaurants: Restaurant[]) {
    // Prevents unnecessary map zoom
    if (!newRestaurants.length) {
      return;
    }

    this.deleteMarkers();
    this.map.setZoom(12);

    for (const restaurant of newRestaurants) {
      if (!this.$services.restaurant.isRestaurantOpen(restaurant) || !restaurant.onlineOrderingEnabled) {
        this.addPin(restaurant, this.getClosedPinType(restaurant));
      } else if (this.favoriteRestaurantIds[restaurant.restaurantNumber] != null) {
        this.addPin(restaurant, this.getFavPinType(restaurant));
      } else {
        this.addPin(restaurant, this.getDefaultPinType(restaurant));
      }
    }
  }

  onDotsDrag(event: MouseEvent) {
    if (event.type === 'mousedown' || event.type === 'touchstart') {
      this.dotsAreDragging = true;
    }

    event.stopPropagation();
    event.preventDefault();
  }

  mouseUp(event: MouseEvent) {
    if (!this.dotsAreDragging) {
      return;
    }

    event.stopPropagation();
    this.dotsAreDragging = false;
  }

  mouseMove(event: MouseEvent) {
    if (!this.dotsAreDragging) {
      return;
    }
    event.stopPropagation();

    this.adjustMapHeight(event.pageY);
  }

  touchMove(event: TouchEvent) {
    if (!this.dotsAreDragging) {
      return;
    }

    event.stopPropagation();
    this.adjustMapHeight(event.changedTouches[0].pageY);
  }

  adjustMapHeight(height: number) {
    const maxHeight = window.innerHeight * 0.75;
    this.mobileMapHeight = height - 11 > maxHeight ? maxHeight : height - 11; // minus half the element height
    this.refreshMapHeightStyles();
  }

  refreshMapHeightStyles() {
    this.mapStyles =
      window.innerWidth >= UiConstants.breakpoints.tablet
        ? { height: '100%' }
        : {
          height: `${this.mobileMapHeight + this.mobileMapOffset}px`,
          flex: `0 0 ${this.mobileMapHeight + this.mobileMapOffset}px`,
        };
  }

  // Pin precedence from highest to lowest:
  // digital kitchen, chipotlane, walk-up, default
  getClosedPinType(restaurant: Restaurant): string {
    return restaurant.digitalKitchen ? PIN_TYPE.DIGITAL_KITCHEN_CLOSED : restaurant.chipotlanePickupEnabled ? PIN_TYPE.CHIPOTLANE_CLOSED : restaurant.walkupWindowEnabled ? PIN_TYPE.WALKUP_WINDOW_CLOSED : PIN_TYPE.CLOSED;
  }

  getFavPinType(restaurant: Restaurant): string {
    return restaurant.digitalKitchen ? PIN_TYPE.DIGITAL_KITCHEN_FAV : restaurant.chipotlanePickupEnabled ? PIN_TYPE.CHIPOTLANE_FAV : restaurant.walkupWindowEnabled ? PIN_TYPE.WALKUP_WINDOW_FAV : PIN_TYPE.FAVORITE;
  }

  getDefaultPinType(restaurant: Restaurant): string {
    return restaurant.digitalKitchen ? PIN_TYPE.DIGITAL_KITCHEN : restaurant.chipotlanePickupEnabled ? PIN_TYPE.CHIPOTLANE : restaurant.walkupWindowEnabled ? PIN_TYPE.WALKUP_WINDOW : PIN_TYPE.DEFAULT;
  }
}
